import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	CfCaptchaWidget,
	useCfTurnstileChallenge
} from '@/components/CfCaptchaWidget/index.js';
import ErrorMessage from '@/components/form/ErrorMessage';
import Input from '@/components/form/Input';
import { useGetErrorMessage } from '@/hooks';
import { useLazyFetch } from '@/hooks/fetch';
import Account from '@/icons/account.svg';
import authModel from '@/models/auth';
import { AUTH_MODAL_TYPES, openAuthModal } from '@/store/profile';

import Description from './components/Description';
import TextLink from './components/TextLink';
import Title from './components/Title';

function RecoverPassword() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const schema = Yup.object({
		email: Yup.string().email().required()
	});
	const getErrorMessage = useGetErrorMessage();

	const methods = useForm({
		defaultValues: { email: '' },
		resolver: yupResolver(schema)
	});

	const {
		cfChallengeToken,
		cfChallengeCompleted,
		cfIdempotencyKey,
		setCfIdempotencyKey,
		triggerChallenge
	} = useCfTurnstileChallenge({
		action: 'recover_password'
	});

	const {
		fetch: changePassword,
		isSuccess,
		isLoading,
		error
	} = useLazyFetch(authModel.changePassword.bind(authModel), {
		onError(err) {
			if (err?.data?.cfIdempotencyKey) {
				setCfIdempotencyKey(err.data.cfIdempotencyKey);
			}
		}
	});
	const errorMessage = getErrorMessage(error);

	const email = methods.watch('email');

	function onSubmit(values) {
		return changePassword({
			...values,
			cfChallengeToken,
			cfIdempotencyKey
		});
	}

	useEffect(() => {
		if (email.includes('@')) {
			triggerChallenge();
		}
	}, [email]);

	useEffect(() => {
		if (error?.data?.cfIdempotencyKey) {
			setCfIdempotencyKey(error.data.cfIdempotencyKey);
		}
	}, [error]);

	return (
		<>
			<Title>{t('authModal.forgotPassword.title')}</Title>

			{isSuccess ? (
				<Description>{t('authModal.forgotPassword.success')}</Description>
			) : (
				<>
					<Description>
						{t('authModal.forgotPassword.description')}
					</Description>

					<FormProvider {...methods}>
						<form
							onSubmit={methods.handleSubmit(onSubmit)}
							className="mt-4 flex flex-col gap-5 md:mt-6"
						>
							<Input
								name="email"
								placeholder={t('authModal.emailPlaceholder')}
								StartIcon={Account}
							/>

							<CfCaptchaWidget />

							<Button
								type="submit"
								className="w-full"
								loading={isLoading}
								disabled={!cfChallengeCompleted}
							>
								{t('authModal.forgotPassword.resetPassword')}
							</Button>
							{errorMessage && (
								<ErrorMessage>{errorMessage}</ErrorMessage>
							)}
						</form>
					</FormProvider>
				</>
			)}

			<TextLink
				disabled={isLoading}
				text={t('authModal.didYouRemember')}
				linkText={t('authModal.signIn')}
				onClick={() =>
					dispatch(
						openAuthModal({
							type: AUTH_MODAL_TYPES.LOGIN
						})
					)
				}
			/>
		</>
	);
}

export default RecoverPassword;
